import { marked } from 'marked';
import DOMPurify from 'dompurify';

/* -------------------------- helpers for {attrs} -------------------------- */

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function parseAttrList(src = '') {
  // Supports: {.c1 .c2 #id key=value key="value" data-x=1 disabled}
  const attrs = {};
  const classes = [];
  // tokenise by spaces but respect quotes
  const re = /(?:("[^"]*"|'[^']*'|[^\s"']+))/g;
  const parts = (src.match(re) || []).map(t => t.replace(/^['"]|['"]$/g, ''));

  for (const token of parts) {
    if (!token) continue;
    if (token[0] === '.') { classes.push(token.slice(1)); continue; }
    if (token[0] === '#') { attrs.id = token.slice(1); continue; }

    const eq = token.indexOf('=');
    if (eq === -1) {
      attrs[token] = true; // boolean attribute
    } else {
      const k = token.slice(0, eq);
      let v = token.slice(eq + 1).replace(/^['"]|['"]$/g, '');
      attrs[k] = v;
    }
  }

  if (classes.length) {
    attrs.class = [attrs.class, ...classes].filter(Boolean).join(' ');
  }
  return attrs;
}

function attrsToString(attrs) {
  return Object.entries(attrs)
    .map(([k, v]) => (v === true ? k : `${k}="${escapeHtml(String(v))}"`))
    .join(' ');
}

function isSameDomain(href) {
  try {
    const u = new URL(href, window.location.origin);
    return u.hostname === window.location.hostname;
  } catch {
    return false;
  }
}

/* ---------------------- your existing functions kept --------------------- */

/**
 * Parse markdown-style bold text (**text**) and convert to HTML
 * @param {string} text - The text to parse
 * @returns {string} - HTML string with bold formatting
 */
export function parseMarkdownBold(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Parse multiple paragraphs with markdown bold formatting
 * @param {Array} paragraphs - Array of paragraph strings
 * @returns {Array} - Array of HTML strings with bold formatting
 */
export function parseMarkdownBoldInParagraphs(paragraphs) {
  if (!Array.isArray(paragraphs)) {
    return paragraphs;
  }
  return paragraphs.map(paragraph => parseMarkdownBold(paragraph));
}

/**
 * Parse markdown-style anchor link and optional {attrs}:
 * [text](url){.class #id data-x=1}
 * Keeps your internal/external behavior.
 */
export function parseMarkdownAnchor(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // [text](url "optional title"){attr list}
  const re = /\[([^\]]+?)\]\(([^)\s]+(?:\s+"[^"]*")?)\)(?:\{([^}]+)\})?/g;

  return text.replace(re, (match, linkText, urlAndMaybeTitle, attrSrc) => {
    try {
      // Split optional title: (url "title")
      let href = urlAndMaybeTitle;
      let title = null;
      const tMatch = /^(.*?)\s+"([^"]+)"\s*$/.exec(urlAndMaybeTitle);
      if (tMatch) {
        href = tMatch[1];
        title = tMatch[2];
      }

      const same = isSameDomain(href);
      const pageMatch = /^\/pages\/(\d+)(?:[/?#].*)?$/.exec(href);
      const attrs = parseAttrList(attrSrc);

      if (title) attrs.title = title;

      if (same && pageMatch) {
        // Use proper /pages/ URL for crawlability, linkHandler will intercept the click
        attrs.href = `/pages/${pageMatch[1]}`;
        attrs['data-slide-index'] = pageMatch[1];
      } else {
        attrs.href = href;
        if (!same) {
          attrs.target = '_blank';
          attrs.rel = 'noopener noreferrer';
        }
      }

      const attrStr = attrsToString(attrs);
      return `<a ${attrStr}>${linkText}</a>`;
    } catch (e) {
      console.warn('Error parsing URL:', urlAndMaybeTitle, e);
      return match;
    }
  });
}

/**
 * Process content string into parsed paragraphs with markdown formatting
 * Handles HTML lists and markdown formatting, ignoring markdown within lists
 * (Keeps your logic, but now supports {attrs} on links too.)
 * @param {string} content - Raw content string
 * @returns {Array} - Array of HTML strings with formatting
 */
export function processContentToParagraphs(content) {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const blocks = content.split(/\n\s*\n/);
  const result = [];

  for (const block of blocks) {
    const trimmedBlock = block.trim();
    if (!trimmedBlock) continue;

    const isListBlock = /^\s*(<[uo]l>|<li>|[-*+]\s|\d+\.\s)/i.test(trimmedBlock);

    if (isListBlock) {
      result.push(trimmedBlock);
    } else {
      const lines = trimmedBlock.split('\n').map(line => line.trim());

      for (const line of lines) {
        if (!line) continue;
        let processedLine = line;
        processedLine = parseMarkdownAnchor(processedLine); // now handles {attrs}
        processedLine = parseMarkdownBold(processedLine);
        result.push(processedLine);
      }
    }
  }

  return result;
}

/* --------------------- marked plugin for {attrs} links -------------------- */

const linkAttrsPlugin = {
  extensions: [
    {
      name: 'linkWithAttrs',
      level: 'inline',
      start(src) {
        return src.indexOf('[');
      },
      tokenizer(src) {
        // [text](url "title"){attrs}
        const rule = /^\[([^\]]+?)\]\(([^)\s]+(?:\s+"[^"]*")?)\)\{([^}]+)\}/;
        const m = rule.exec(src);
        if (!m) return;

        const text = m[1];
        let href = m[2];
        let title = null;
        const tMatch = /^(.*?)\s+"([^"]+)"\s*$/.exec(href);
        if (tMatch) {
          href = tMatch[1];
          title = tMatch[2];
        }

        return {
          type: 'linkWithAttrs',
          raw: m[0],
          href,
          title,
          attrs: parseAttrList(m[3].trim()),
          tokens: this.lexer.inlineTokens(text),
        };
      },
      renderer(token) {
        const same = isSameDomain(token.href);
        const pageMatch = /^\/pages\/(\d+)(?:[/?#].*)?$/.exec(token.href);

        const attrs = { ...token.attrs };
        if (token.title) attrs.title = token.title;

        if (same && pageMatch) {
          // Use proper /pages/ URL for crawlability, linkHandler will intercept the click
          attrs.href = `/pages/${pageMatch[1]}`;
          attrs['data-slide-index'] = pageMatch[1];
        } else {
          attrs.href = token.href;
          if (!same) {
            attrs.target = '_blank';
            attrs.rel = 'noopener noreferrer';
          }
        }

        const attrStr = attrsToString(attrs);
        return `<a ${attrStr}>${this.parser.parseInline(token.tokens)}</a>`;
      },
    },
  ],
  renderer: {
    // Fallback for standard links (no {attrs})
    link(href, title, text) {
      const same = isSameDomain(href);
      const pageMatch = /^\/pages\/(\d+)(?:[/?#].*)?$/.exec(href);

      const attrs = {};
      if (title) attrs.title = title;

      if (same && pageMatch) {
        // Use proper /pages/ URL for crawlability, linkHandler will intercept the click
        attrs.href = `/pages/${pageMatch[1]}`;
        attrs['data-slide-index'] = pageMatch[1];
      } else {
        attrs.href = href;
        if (!same) {
          attrs.target = '_blank';
          attrs.rel = 'noopener noreferrer';
        }
      }

      const attrStr = attrsToString(attrs);
      return `<a ${attrStr}>${text}</a>`;
    },
  },
};

// Register once at module init
marked.use(linkAttrsPlugin);

/* ----------------- your existing marked + DOMPurify pipeline -------------- */

// Packages content with markdown and DOMPurify
export function processContent(content) {
  try {
    const dirty = content ? marked.parse(content) : '';
    // ensure our custom data attribute passes through
    return DOMPurify.sanitize(dirty, {
      ADD_ATTR: ['data-slide-index'],
    });
  } catch (e) {
    console.error('Error processing markdown:', e);
    return content || ''; // Return original content or empty string
  }
}
